import { getId } from '@/store/utils'
import { normalizeHexColor } from '../color'
import type { XmlImportSeoData } from '../types'
import {
  getChildElements,
  getFirstChildElement,
  getOptionalChildText,
} from './dom'
import { parseNodeTags } from './tags'

export interface ParsedXmlImportBlockItem {
  id: string
  title: string
  content?: string
  wireframes?: string[]
  colorHex?: string
}

export interface ParsedXmlImportNodeItem {
  id: string
  title: string
  seo: XmlImportSeoData
  tags: string[]
  colorHex?: string
  blocks: ParsedXmlImportBlockItem[]
  children: ParsedXmlImportNodeItem[]
}

const parseElementColor = (element: Element): string | null =>
  normalizeHexColor(element.getAttribute('color') || '')

const parseWireframes = (value: string | null): string[] => {
  if (!value) {
    return []
  }

  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

const parseSeo = (nodeElement: Element): XmlImportSeoData => {
  const seoElement = getFirstChildElement(nodeElement, ['seo'])
  if (!seoElement) {
    return {}
  }

  const title = getOptionalChildText(seoElement, ['seo_title']) || ''
  const slug = getOptionalChildText(seoElement, ['slug']) || ''
  const description = getOptionalChildText(seoElement, ['description']) || ''
  const h1 = getOptionalChildText(seoElement, ['h1']) || ''
  const keywords = getOptionalChildText(seoElement, ['keywords']) || ''
  const pageIntent = getOptionalChildText(seoElement, ['page_intent']) || ''

  return {
    title,
    slug,
    description,
    h1,
    keywords,
    pageIntent,
  }
}

const parseBlocks = (nodeElement: Element): ParsedXmlImportBlockItem[] => {
  const blocksElement = getFirstChildElement(nodeElement, ['blocks'])
  if (!blocksElement) {
    return []
  }

  return getChildElements(blocksElement, 'block').map((blockElement) => {
    const title =
      getOptionalChildText(blockElement, ['block_title']) || 'Untitled'
    const content = getOptionalChildText(blockElement, ['content'])
    const wireframes = parseWireframes(
      getOptionalChildText(blockElement, ['wireframe'])
    )
    const colorHex = parseElementColor(blockElement)

    return {
      id: getId(),
      title,
      ...(content ? { content } : {}),
      ...(wireframes.length ? { wireframes } : {}),
      ...(colorHex ? { colorHex } : {}),
    }
  })
}

const parseChildren = (nodeElement: Element): ParsedXmlImportNodeItem[] => {
  const childrenElement = getFirstChildElement(nodeElement, ['children'])

  if (!childrenElement) {
    return []
  }

  return getChildElements(childrenElement, 'node').map(parseNode)
}

export const parseNode = (nodeElement: Element): ParsedXmlImportNodeItem => {
  const seo = parseSeo(nodeElement)
  const tags = parseNodeTags(nodeElement)
  const title = getOptionalChildText(nodeElement, ['node_title']) || 'Untitled'
  const colorHex = parseElementColor(nodeElement)
  const children = parseChildren(nodeElement)

  return {
    id: getId(),
    title,
    seo,
    tags,
    ...(colorHex ? { colorHex } : {}),
    blocks: parseBlocks(nodeElement),
    children,
  }
}
